/**
 * Auto collision-avoidance for edge labels (#2048).
 *
 * Edge labels are drawn by `renderEdge` at each edge's midpoint with no
 * awareness of neighbouring labels or node rectangles, so on dense diagrams two
 * adjacent labels overlap (system-top) or a short edge's centred label clips
 * into an adjacent node card (drill-down). ADR-1184 deferred this "auto
 * collision detection" while shipping the manual `label-position` /
 * `label-offset` lever; this module implements the deferred auto pass.
 *
 * The pass is a **bounded, deterministic** post-pass: it nudges only labels that
 * collide, perpendicular to the edge, choosing the first offset (out of a small
 * capped candidate set) that clears every node rect, every already-placed label
 * and every *foreign* edge polyline. A label that already sits clear at its
 * default anchor is left untouched — so non-colliding diagrams stay
 * byte-identical (ADR-1184's compatibility promise, now conditional on "no
 * collision"). Author-positioned labels are not eligible to move; author intent
 * wins (ADR-1184 precedence). They still act as obstacles so auto labels route
 * around them.
 *
 * Edge polylines joined the obstacle set in #2360: the original pass knew only
 * about node cards and sibling labels, so it could lift a label off a card and
 * drop it squarely onto another edge's line — and a label that never touched a
 * card was never examined at all. An edge's *own* polyline is exempt: a label is
 * supposed to sit on the line it names.
 *
 * Frame borders joined in #3155: a nudged label ends off the border of every
 * frame (a group frame, the drill-down frame) and on the same side of it as
 * its default. The rule reads where the label lands, not the path to it, so a
 * long nudge may still hop a narrow frame or a notch of an L-shaped one and
 * land on the right side. The frame's *area* is still not an obstacle
 * (labels legitimately live inside it), so what is tested is the border line
 * and which side of it the label is on, measured against the label's default
 * box. A label whose default already sits outside a frame (its own run left
 * the frame) stays outside; one whose default is inside stays inside.
 *
 * Overlap is *measured*, not eyeballed: `countLabelPenetrations` /
 * `countLabelOverlaps` / `countLabelLinePenetrations` /
 * `countLabelFrameCrossings` give the numeric guards the tests assert on
 * (TPL-1927, the label analogue of the edge-routing penetration guards).
 */
import type { Point, Rect } from "./edge-geometry.js";
import type { ContainerRect, LayoutEdge, LayoutNode } from "./layout-types.js";
import type { ResolvedEdgeStyle } from "../types/style.js";
import { estimateTextWidth } from "./rendering-constants.js";
import { labelAnchorWithSegment, ownLabelSegment, resolveLabelPosition } from "./edge-routing.js";
import { segmentCrossesRect } from "./edge-geometry.js";
import { BoxGrid, chooseCellSize } from "./spatial-grid.js";
import { canvasLabel } from "./edge-label-disclosure.js";
import { drawnFrame } from "./frame-geometry.js";

/**
 * One edge label offered to the placement pass. `anchor` is the label's default
 * draw position (already including any parallel-bundle slide, ADR-1185), i.e.
 * exactly where `renderEdge` would place it absent this pass.
 */
export interface LabelInput {
  /** Edge index — the key of the returned override map, matching `layoutResult.edges`. */
  index: number;
  /** Default anchor (bundle slide applied), before any auto nudge. */
  anchor: Point;
  /** Direction of the local polyline segment the anchor sits on; the nudge axis is perpendicular to it. */
  dir: Point;
  /** Estimated rendered width of the label text in px. */
  width: number;
  /** Label font size in px (drives box height and the nudge step). */
  fontSize: number;
  /** Whether this label may move. `false` for author-positioned labels (obstacle only). */
  eligible: boolean;
  /**
   * Whether the pass may leave this label off the canvas when no candidate
   * seats it clear (`label-display: auto`, #3022). A deferred label is not
   * drawn and is not an obstacle for the labels placed after it.
   */
  deferrable?: boolean;
  /**
   * Placed after every other label. A ghost edge is dimmed context: under
   * `auto` its label may take only the room the real labels left, so it yields
   * to them instead of pushing them aside.
   */
  late?: boolean;
  /**
   * Set by `resolveLabelPlacements`: no candidate seated this `deferrable`
   * label clear, so the canvas leaves it off. Written back onto the input
   * rather than returned, which keeps the pass's signature as ADR-2360's
   * assumption records it.
   */
  deferred?: boolean;
}

/**
 * One edge polyline offered to the placement pass as an obstacle (#2360). A
 * label never treats the line of its *own* edge as an obstacle, so `index` is
 * carried through to be matched against `LabelInput.index`.
 */
export interface EdgeLine {
  /** Edge index — the same key space as `LabelInput.index` and `layoutResult.edges`. */
  index: number;
  /** from → waypoints → to, exactly the polyline `renderEdge` strokes. */
  points: Point[];
  /**
   * Half the edge's `stroke-width`. `points` is the *centreline*, but what
   * obscures the text is the painted stroke, which extends this far either side
   * of it. Tests inflate the label box by this much, so a thick edge whose
   * centreline just misses the box still reads as a collision.
   */
  halfStroke: number;
  /**
   * Axis-aligned bounds of `points`, already grown by `halfStroke`. The
   * candidate loop runs every label box against every foreign line, so a cheap
   * bounds reject keeps the segment math off the (large) majority of pairs that
   * cannot possibly touch.
   */
  bounds: Rect;
}

/**
 * One frame's border offered to the placement pass as an obstacle (#3155).
 * Built only by `frameBorder`, so the renderer and the tests measure the same
 * outline.
 */
export interface FrameBorder {
  /**
   * The stroked outline as a closed polyline, packaged like an edge line so the
   * label box is tested against the painted stroke with the same
   * strict-interior segment test (TPL-1927). Its `index` is `-1`, which no
   * label carries, so it is never mistaken for a label's own line.
   */
  line: EdgeLine;
  /** The rects the frame covers: what "inside the frame" means for the side test. */
  pieces: readonly Rect[];
}

/**
 * Package a frame as a placement obstacle: the outline the canvas strokes and
 * the stroke's width, plus the rects the frame covers.
 */
export function frameBorder(frame: ContainerRect, strokeWidth: number): FrameBorder {
  const { outline, pieces } = drawnFrame(frame);
  return { line: edgeLine(-1, outline, strokeWidth), pieces };
}

/** True if `p` lies in one of the frame's rects (border included). */
function insideFrame(p: Point, pieces: readonly Rect[]): boolean {
  return pieces.some(
    (r) => p.x >= r.x && p.x <= r.x + r.width && p.y >= r.y && p.y <= r.y + r.height,
  );
}

function boxCentre(box: Rect): Point {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

/**
 * A frame border as one label sees it: the border, and which side of it the
 * label's default box is on (by its centre). Every candidate has to stay on
 * that side without touching the stroke.
 */
interface NearFrame {
  border: FrameBorder;
  inside: boolean;
}

/**
 * True if a label box placed at `box` breaks the frame rule against `frame`:
 * the border's stroke runs through it, or it sits on the other side of the
 * border than the label's default. Once the stroke is clear the box lies
 * wholly on one side, so its centre answers for all of it.
 */
function leavesFrameSide(box: Rect, frame: NearFrame): boolean {
  if (boxCrossedByLine(box, frame.border.line)) return true;
  return insideFrame(boxCentre(box), frame.border.pieces) !== frame.inside;
}

/**
 * Count of labels whose final box crosses a frame border or ended on the other
 * side of one than its default box (#3155). Counted per label, like the other
 * guards. A default box that already straddles a border counts until the label
 * is moved off it. Pass only labels the pass may move: an author-positioned
 * label (ADR-1184) stays where the author put it, border or not.
 */
export function countLabelFrameCrossings(
  labels: { defaultBox: Rect; box: Rect }[],
  frames: FrameBorder[],
): number {
  let n = 0;
  for (const { defaultBox, box } of labels) {
    const side = boxCentre(defaultBox);
    if (
      frames.some((border) =>
        leavesFrameSide(box, { border, inside: insideFrame(side, border.pieces) }),
      )
    ) {
      n++;
    }
  }
  return n;
}

/**
 * The frames a label's bounded search can be affected by, each with the side
 * the default box is on. Every candidate's centre lies within `maxShift·√2` of
 * the default centre, so a candidate can reach the border's stroke only when
 * the border's centreline lies within that plus the half-diagonal of the box
 * grown by half the stroke (the stroke test grows the box that much), and it
 * can change side only by crossing the border on the way. A frame farther
 * than that is one the label is on the right side of at every candidate. The
 * prune is exact, not close: measured with the bare half-diagonal it would
 * drop a border a box corner reaches by less than the stroke.
 */
function reachableFrames(frames: FrameBorder[], box: Rect, maxShift: number): NearFrame[] {
  if (frames.length === 0) return [];
  const centre = boxCentre(box);
  const out: NearFrame[] = [];
  for (const border of frames) {
    const h = border.line.halfStroke;
    const reach = maxShift * Math.SQRT2 + Math.hypot(box.width / 2 + h, box.height / 2 + h);
    // The polyline distance is to the stroke's edge (it takes `h` off), so the
    // centreline bound `reach` is `reach - h` here. The bounds are grown by `h`
    // as a square, so their distance is never more than the polyline's and the
    // cheap reject stays safe against the same bound.
    if (pointToRectDistance(centre, border.line.bounds) > reach - h) continue;
    if (pointToPolylineDistance(centre, border.line) > reach - h) continue;
    out.push({ border, inside: insideFrame(centre, border.pieces) });
  }
  return out;
}

interface LabelPlacementOptions {
  /** Max nudge steps to try on each side of the edge (candidates = 2·maxSteps + 1). */
  maxSteps?: number;
}

// Cap on nudge steps per side. The resolver stops at the first (smallest) clear
// offset, so this is only the search bound — easy labels clear at 1–2 steps; the
// higher cap lets a wide label in a dense cluster still find open space
// (≈ 6·step ≈ 90px), rather than being left clipping a node.
const DEFAULT_MAX_STEPS = 6;

/**
 * Cost weights for a candidate placement. A collision hides the text outright;
 * ambiguity leaves it readable but attached to the wrong line by eye. Two-to-one
 * makes any clear spot beat any colliding one, while still steering the search
 * towards clear spots that stay nearest their own edge. See `candidateCost`.
 */
const COLLISION_COST = 2;
const AMBIGUITY_COST = 1;

/**
 * Tolerance the spatial prefilter adds around a query box. The exact tests are
 * strict-interior with this same epsilon (`rectsOverlap`, `segmentCrossesRect`),
 * so a box the grid is asked about is grown by it and the grid can only ever
 * return a superset of what the exact tests accept.
 */
const GRID_EPS = 1e-6;

/**
 * Sans-serif glyphs render at roughly 0.6× the font size wide. `estimateTextWidth`
 * takes an absolute per-char width, so callers pass `fontSize * this`.
 */
const EDGE_LABEL_CHAR_WIDTH_RATIO = 0.6;

/** Estimated width of an edge label text, matching what the placement pass measures. */
function edgeLabelWidth(label: string, fontSize: number): number {
  return estimateTextWidth(label, fontSize * EDGE_LABEL_CHAR_WIDTH_RATIO);
}

/**
 * Build the placement pass inputs from a laid-out view: one `LabelInput` per
 * labelled edge (default anchor + chord + estimated box, eligible unless the
 * author positioned it), the node-card obstacle rects, and one `EdgeLine` per
 * drawn edge. Shared by the renderer and its tests so both measure the exact
 * same geometry.
 *
 * `edgeLines` covers **every** non-ghost/cyclic edge, labelled or not: an
 * unlabelled edge's stroke obscures label text just as thoroughly as a labelled
 * one's (#2360). A cyclic edge whose label may be left off the canvas
 * (`label-display: auto`) joins both lists; a ghost edge joins `inputs` only.
 */
export function buildLabelInputs(
  edges: LayoutEdge[],
  nodes: Map<string, LayoutNode>,
  styleFor: (edge: LayoutEdge, index: number) => ResolvedEdgeStyle,
): { inputs: LabelInput[]; nodeRects: Rect[]; edgeLines: EdgeLine[] } {
  const nodeRects: Rect[] = [...nodes.values()].map((n) => ({
    x: n.x,
    y: n.y,
    width: n.width,
    height: n.height,
  }));
  const inputs: LabelInput[] = [];
  const edgeLines: EdgeLine[] = [];
  // A cyclic edge is drawn as a full-strength line, so a label under it is as
  // unreadable as one under any other line. Its line obstructs whenever the
  // canvas has a label it may leave off (`label-display: auto`, #3022),
  // whatever the cyclic edge's own label is: "may leave off" must mean "would
  // have been unreadable", and an unlabelled cyclic line is just as opaque.
  const cyclicLines: EdgeLine[] = [];
  edges.forEach((edge, index) => {
    const style = styleFor(edge, index);
    const points: Point[] = [edge.fromPoint, ...(edge.waypoints ?? []), edge.toPoint];
    // The same anchor `renderEdge` will draw, override included (were they to
    // disagree, the pass would nudge a label off a collision it is not at). Found
    // before the text: a paired half's label carries its segment's direction (#3027).
    const { anchor, segDir } = labelAnchorWithSegment(
      points,
      resolveLabelPosition(edge, style),
      style.labelOffsetX,
      style.labelOffsetY,
      ownLabelSegment(edge),
    );
    const drawn = canvasLabel(edge, style, segDir);
    if (edge.cyclic === true && edge.ghost !== true) {
      cyclicLines.push(edgeLine(index, points, style.strokeWidth));
    }
    // Ghost/cyclic edges are peripheral (dimmed / back-arc styled) and sit
    // outside the "real" geometry every other renderer pass reasons about —
    // crossing-marks, port fan-out, channel/group routing and bundle nudging
    // all skip them (ADR-968). They stay out of this pass too, with one
    // exception: a label the canvas may leave off (`label-display: auto`,
    // #3022) has to be seen by the pass, because a label the pass never looks
    // at is drawn into whatever it lands on.
    const peripheral = edge.ghost === true || edge.cyclic === true;
    if (peripheral && drawn?.display !== "auto") return;
    // A dimmed ghost line never displaces a real label, in any mode (#2360).
    // A cyclic line is added below, once for the whole canvas.
    if (!edge.ghost && !edge.cyclic) edgeLines.push(edgeLine(index, points, style.strokeWidth));
    // Nothing to place: no label, or one the canvas never draws.
    if (drawn === undefined || drawn.display === "hover") return;
    inputs.push({
      index,
      anchor,
      // Nudge perpendicular to the *local* segment the anchor sits on, not the
      // overall from→to chord — otherwise a bent / waypoint route's label would
      // be shifted at a skewed angle relative to the line it labels (#2048).
      dir: segDir,
      // Measure the text the canvas draws, not the authored one: a label
      // truncated to `label-max-chars` is the narrower box the nudge can clear.
      width: edgeLabelWidth(drawn.text, style.fontSize),
      fontSize: style.fontSize,
      // Author-positioned labels (non-default label-position/offset) are not
      // eligible to move — author intent wins (ADR-1184 precedence).
      eligible: style.labelPosition === 0.5 && style.labelOffsetX === 0 && style.labelOffsetY === 0,
      deferrable: drawn.display === "auto",
      late: edge.ghost === true,
    });
  });
  if (inputs.some((input) => input.deferrable === true)) {
    edgeLines.push(...cyclicLines);
    edgeLines.sort((a, b) => a.index - b.index);
  }
  return { inputs, nodeRects, edgeLines };
}

/**
 * Package a polyline as a placement obstacle, precomputing the half-stroke and
 * the grown bounds. The one construction site for `EdgeLine`, so the renderer
 * and the tests cannot end up measuring against differently-built geometry.
 */
export function edgeLine(index: number, points: Point[], strokeWidth: number): EdgeLine {
  const halfStroke = strokeWidth / 2;
  return { index, points, halfStroke, bounds: polylineBounds(points, halfStroke) };
}

/**
 * Axis-aligned bounds of a polyline, grown by `pad` on every side. Degenerate
 * (zero width or height) for an axis-parallel run at `pad === 0`.
 */
function polylineBounds(points: Point[], pad: number): Rect {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return {
    x: minX - pad,
    y: minY - pad,
    width: maxX - minX + pad * 2,
    height: maxY - minY + pad * 2,
  };
}

/** Axis-aligned bounds enclosing every rect; a zero rect at the origin when there are none. */
function unionBounds(rects: Rect[]): Rect {
  if (rects.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const r of rects) {
    if (r.x < minX) minX = r.x;
    if (r.y < minY) minY = r.y;
    if (r.x + r.width > maxX) maxX = r.x + r.width;
    if (r.y + r.height > maxY) maxY = r.y + r.height;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Axis-aligned bounding box of a label drawn at `anchor`. Mirrors how
 * `renderEdge` emits the text: `text-anchor="middle"` (centred on `anchor.x`)
 * with the baseline at `anchor.y - 6`. The box spans the ascender height above
 * the baseline plus a small descender/padding below, and a little horizontal
 * padding so touching-but-legible cases still read as "clear".
 */
export function labelBox(anchor: Point, width: number, fontSize: number): Rect {
  const padX = 2;
  const baseline = anchor.y - 6;
  const top = baseline - fontSize;
  return {
    x: anchor.x - width / 2 - padX,
    y: top,
    width: width + padX * 2,
    height: fontSize + 4,
  };
}

/** True if two axis-aligned rectangles share positive-area interior overlap (touching does not count). */
function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width - 1e-6 &&
    a.x + a.width > b.x + 1e-6 &&
    a.y < b.y + b.height - 1e-6 &&
    a.y + a.height > b.y + 1e-6
  );
}

/** Count of `boxes` entries that overlap at least one obstacle rect (label↔node penetrations). */
export function countLabelPenetrations(boxes: Rect[], obstacles: Rect[]): number {
  let n = 0;
  for (const box of boxes) {
    if (obstacles.some((o) => rectsOverlap(box, o))) n++;
  }
  return n;
}

/**
 * Inclusive AABB intersection, used only as a cheap reject before the exact
 * segment test. Inclusive (not strict-interior like `rectsOverlap`) because an
 * axis-parallel polyline's bounds are degenerate — a horizontal line has zero
 * height, and a strict test would reject every one of them.
 */
function boundsIntersect(a: Rect, b: Rect): boolean {
  return (
    a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y
  );
}

/**
 * True if any segment of `line`'s painted stroke crosses the interior of `box`
 * — a label drawn there would have the stroke running through its text. Uses
 * the same strict-interior `segmentCrossesRect` the routers decide routes with,
 * so the pass and the tests that assert zero can never disagree (TPL-1927).
 *
 * The stroke is tested by growing the label box by the line's half-width rather
 * than by thickening the line: a segment-vs-rect test is exact, and inflating
 * the rect is the same predicate with none of the capsule geometry. (Corners
 * are then square rather than round, which over-reports by a fraction of the
 * stroke width diagonally — the safe direction for a legibility guard.)
 */
function boxCrossedByLine(box: Rect, line: EdgeLine): boolean {
  if (!boundsIntersect(box, line.bounds)) return false;
  const grown = growBox(box, line.halfStroke);
  for (let i = 0; i < line.points.length - 1; i++) {
    if (segmentCrossesGrownBox(line.points[i], line.points[i + 1], grown)) return true;
  }
  return false;
}

/** `box` grown by `pad` on every side — the box itself when `pad` is 0. */
function growBox(box: Rect, pad: number): Rect {
  return pad === 0
    ? box
    : { x: box.x - pad, y: box.y - pad, width: box.width + pad * 2, height: box.height + pad * 2 };
}

/**
 * One segment's share of `boxCrossedByLine`: does the segment `a`–`b` cross the
 * interior of the (already grown) box? An inclusive bounds reject runs first —
 * a segment whose bounds lie strictly outside the box has no point inside it,
 * so the clip would return false anyway; this only skips its work (#2760).
 */
function segmentCrossesGrownBox(a: Point, b: Point, grown: Rect): boolean {
  if (
    Math.max(a.x, b.x) < grown.x ||
    Math.min(a.x, b.x) > grown.x + grown.width ||
    Math.max(a.y, b.y) < grown.y ||
    Math.min(a.y, b.y) > grown.y + grown.height
  ) {
    return false;
  }
  return segmentCrossesRect(a, b, grown);
}

/**
 * Count of labels whose box is crossed by at least one **foreign** edge
 * polyline (label↔line penetrations, #2360). A label sitting on its own edge's
 * line is not counted — that is where it belongs. Counted per label, matching
 * how `countLabelPenetrations` counts.
 */
export function countLabelLinePenetrations(
  boxes: { index: number; box: Rect }[],
  lines: EdgeLine[],
): number {
  let n = 0;
  for (const { index, box } of boxes) {
    if (lines.some((line) => line.index !== index && boxCrossedByLine(box, line))) n++;
  }
  return n;
}

/** Count of unordered label-pair overlaps (label↔label collisions). */
export function countLabelOverlaps(boxes: Rect[]): number {
  let n = 0;
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      if (rectsOverlap(boxes[i], boxes[j])) n++;
    }
  }
  return n;
}

/**
 * The lines a label's bounded search can possibly be affected by, so the
 * candidate loop scans a handful instead of the whole diagram.
 *
 * `maxShift` is the largest displacement along either search axis, so every
 * candidate anchor lies within `maxShift·√2` of the default one, and every
 * candidate *box* within that plus its own half-diagonal. A line farther than
 * that from the default anchor can never be crossed. The ambiguity term needs a
 * wider radius: an eligible label's default anchor sits *on* its own line, so at
 * any candidate the own-line distance is at most `maxShift·√2`, and a foreign
 * line that beats it must lie within that of the candidate — hence within twice
 * that of the default anchor. The wider of the two radii is used for both, which
 * keeps the prune exact rather than merely close.
 */
function reachableLines(edgeLines: EdgeLine[], label: LabelInput, maxShift: number): EdgeLine[] {
  if (edgeLines.length === 0) return edgeLines;
  const box = labelBox(label.anchor, label.width, label.fontSize);
  const halfDiagonal = Math.hypot(box.width, box.height) / 2;
  const reach = 2 * maxShift * Math.SQRT2 + halfDiagonal;
  return edgeLines.filter(
    (line) => line.index === label.index || pointToRectDistance(label.anchor, line.bounds) <= reach,
  );
}

/**
 * What a candidate placement costs, in units where a **collision** (node card,
 * committed label, foreign stroke through the text, or a frame border crossed,
 * #3155) is 2 and **ambiguity**
 * (some other edge's line is nearer than the one this label names) is 1.
 *
 * The two are weighted rather than summed flat because they are different kinds
 * of unreadable and one is strictly worse: a label buried under a card or a
 * stroke cannot be read at all, while an ambiguous one is legible but attached
 * to the wrong line by eye. Weighting collisions at 2 makes the resolver prefer
 * *any* clear spot over a colliding one, and among clear spots prefer the ones
 * that still read as belonging to their own edge. Without the ambiguity term the
 * search happily parks a label on the far side of a neighbouring edge, which is
 * how #2360's fix could have traded an unreadable label for a mislabelled one.
 *
 * Collision counting stops as soon as `cap` (the best cost so far) is reached —
 * on dense diagrams that is the difference between rescanning every line for
 * every one of the ~169 candidates and bailing after two hits. The caller makes
 * *two* comparisons, and the early return is safe for both only because a capped
 * return is always `>= cap > 0`: it can neither beat `bestCost` nor be mistaken
 * for the `cost === 0` fast path that accepts a candidate outright. A returned
 * `0` therefore always means genuinely clear and unambiguous.
 *
 * The ambiguity term is deliberately **not** capped. Reaching it already implies
 * `cost < cap` (every collision early-return fires at `cost >= cap`), and it
 * cannot be skipped on the grounds that it might only tie: a candidate sitting
 * at `cap - 1` still wins if it turns out to be unambiguous, so its true value
 * has to be known. Capping it here is what made an ambiguous candidate report
 * `0` and short-circuit the search onto a mislabelled placement (#2413 review).
 */
function candidateCost(
  box: Rect,
  anchor: Point,
  label: LabelInput,
  obstacles: ObstacleIndex,
  lines: LineIndex,
  reachable: EdgeLine[],
  ownLine: EdgeLine | undefined,
  frames: NearFrame[],
  cap: number,
): number {
  // Both obstacle sets are queried through a spatial index (#2760), so only the
  // rects / lines whose bounds can touch `box` reach the exact tests. The
  // *order* the candidates come back in is not the order the old flat loops
  // used, and that does not matter: every collision adds the same
  // `COLLISION_COST`, so a candidate that survives the cap returns the same
  // sum whatever the order, and one that trips it returns the same first
  // multiple of `COLLISION_COST` at or above `cap` — the only two facts the
  // caller reads.
  let cost = 0;
  for (const rect of obstacles.near(box)) {
    if (rectsOverlap(box, rect)) {
      cost += COLLISION_COST;
      if (cost >= cap) return cost;
    }
  }
  // A frame border is not an edge: it costs a collision when the label would
  // land on it or across it, and never takes part in the ambiguity term below,
  // which asks which *edge* the text reads as belonging to.
  for (const frame of frames) {
    if (leavesFrameSide(box, frame)) {
      cost += COLLISION_COST;
      if (cost >= cap) return cost;
    }
  }
  // A label is meant to sit on the line it names, so its own polyline is never
  // an obstacle (#2360): `crossing` leaves it out.
  const crossed = lines.crossing(box, label.index).length;
  for (let n = 0; n < crossed; n++) {
    cost += COLLISION_COST;
    if (cost >= cap) return cost;
  }
  if (ownLine !== undefined && nearestLineIsForeign(anchor, label.index, reachable, ownLine)) {
    cost += AMBIGUITY_COST;
  }
  return cost;
}

/**
 * Node cards and committed label boxes, indexed by bounds (#2760). Boxes are
 * appended as labels commit, so the grid supports insertion; cells are sized
 * by `chooseCellSize` from the cards' longer sides.
 */
class ObstacleIndex {
  private readonly rects: Rect[] = [];
  private readonly grid: BoxGrid;
  private readonly hits: number[] = [];
  private readonly out: Rect[] = [];

  constructor(rects: Rect[], extent: Rect) {
    const sizes = rects.map((r) => Math.max(r.width, r.height));
    const cell = chooseCellSize(sizes, extent.width, extent.height);
    this.grid = new BoxGrid(
      cell,
      extent.x,
      extent.y,
      extent.x + extent.width,
      extent.y + extent.height,
    );
    for (const r of rects) this.add(r);
  }

  add(rect: Rect): void {
    this.grid.insert(this.rects.length, rect.x, rect.y, rect.x + rect.width, rect.y + rect.height);
    this.rects.push(rect);
  }

  /** The obstacles whose bounds can overlap `box` — a superset of those `rectsOverlap` accepts. */
  near(box: Rect): Rect[] {
    this.grid.query(
      box.x - GRID_EPS,
      box.y - GRID_EPS,
      box.x + box.width + GRID_EPS,
      box.y + box.height + GRID_EPS,
      this.hits,
    );
    this.out.length = 0;
    for (const id of this.hits) this.out.push(this.rects[id]);
    return this.out;
  }
}

/**
 * Edge polylines indexed **per segment**, each segment's bounds grown by its
 * stroke half-width — the same growth `boxCrossedByLine` applies (#2760). A
 * long route is then found only near the stretches it actually runs through,
 * not across its whole bounding box. Cells are sized by `chooseCellSize` from
 * the segment lengths. Every drawn line is inserted from the same list the
 * pass reads, never a shape-specific subset (TPL-1954).
 */
class LineIndex {
  private readonly grid: BoxGrid;
  /** Segment id → position in `lines`. */
  private readonly segLine: number[] = [];
  /** Segment id → index of its first point in that line's `points`. */
  private readonly segPoint: number[] = [];
  /** Line → its position in `lines`, for `restrictTo`. */
  private readonly posOf = new Map<EdgeLine, number>();
  /** Per line position, whether the current label's bounded search can reach it (see `restrictTo`). */
  private readonly reachable: Uint8Array;
  /** Per line position, the query that settled it (crossed, excluded) so its other segments are skipped. */
  private readonly settled: Uint32Array;
  private queryId = 0;
  private readonly hits: number[] = [];
  private readonly out: EdgeLine[] = [];

  constructor(private readonly lines: EdgeLine[]) {
    this.reachable = new Uint8Array(lines.length);
    this.settled = new Uint32Array(lines.length);
    const boxes: number[] = [];
    const sizes: number[] = [];
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    lines.forEach((line, pos) => {
      this.posOf.set(line, pos);
      const pad = line.halfStroke;
      for (let i = 0; i < line.points.length - 1; i++) {
        const a = line.points[i];
        const b = line.points[i + 1];
        const x0 = Math.min(a.x, b.x) - pad;
        const y0 = Math.min(a.y, b.y) - pad;
        const x1 = Math.max(a.x, b.x) + pad;
        const y1 = Math.max(a.y, b.y) + pad;
        boxes.push(x0, y0, x1, y1);
        sizes.push(Math.hypot(b.x - a.x, b.y - a.y));
        this.segLine.push(pos);
        this.segPoint.push(i);
        if (x0 < minX) minX = x0;
        if (y0 < minY) minY = y0;
        if (x1 > maxX) maxX = x1;
        if (y1 > maxY) maxY = y1;
      }
    });
    const cell = chooseCellSize(sizes, maxX - minX, maxY - minY);
    this.grid = new BoxGrid(cell, minX, minY, maxX, maxY);
    for (let id = 0; id < this.segLine.length; id++) {
      const k = id * 4;
      this.grid.insert(id, boxes[k], boxes[k + 1], boxes[k + 2], boxes[k + 3]);
    }
  }

  /**
   * Limit `crossing` to the given lines — the ones a label's bounded search
   * can reach (`reachableLines`), so the set of lines examined for a candidate
   * is exactly a subset of the set the unindexed pass examined.
   */
  restrictTo(reachable: EdgeLine[]): void {
    this.reachable.fill(0);
    for (const line of reachable) {
      const pos = this.posOf.get(line);
      if (pos !== undefined) this.reachable[pos] = 1;
    }
  }

  /**
   * The lines whose painted stroke crosses `box` — exactly the lines
   * `boxCrossedByLine` accepts, each once, among those `restrictTo` allowed and
   * excluding the line of edge `ownIndex`. Only the segments the grid found
   * near the box are clipped: a segment whose grown bounds miss the box cannot
   * cross it, so the OR over the near segments equals the OR over all of them.
   */
  crossing(box: Rect, ownIndex: number): EdgeLine[] {
    this.grid.query(
      box.x - GRID_EPS,
      box.y - GRID_EPS,
      box.x + box.width + GRID_EPS,
      box.y + box.height + GRID_EPS,
      this.hits,
    );
    const stamp = ++this.queryId;
    this.out.length = 0;
    for (const id of this.hits) {
      const pos = this.segLine[id];
      if (this.settled[pos] === stamp) continue;
      const line = this.lines[pos];
      if (this.reachable[pos] === 0 || line.index === ownIndex) {
        this.settled[pos] = stamp;
        continue;
      }
      const i = this.segPoint[id];
      if (
        segmentCrossesGrownBox(line.points[i], line.points[i + 1], growBox(box, line.halfStroke))
      ) {
        this.out.push(line);
        this.settled[pos] = stamp;
      }
    }
    return this.out;
  }
}

/**
 * True if some other edge's line runs nearer to `anchor` than the label's own
 * line does — i.e. a reader tracing from the text to the closest stroke lands on
 * the wrong edge. Distances are measured from the anchor rather than the box
 * because the anchor is where the text is centred and where the eye starts.
 */
function nearestLineIsForeign(
  anchor: Point,
  ownIndex: number,
  edgeLines: EdgeLine[],
  ownLine: EdgeLine,
): boolean {
  const ownDist = pointToPolylineDistance(anchor, ownLine);
  for (const line of edgeLines) {
    if (line.index === ownIndex) continue;
    // Cheap reject: nothing inside `line`'s bounds can be nearer than the gap to
    // those bounds, so a line whose bounds are already farther cannot win.
    if (pointToRectDistance(anchor, line.bounds) >= ownDist) continue;
    if (pointToPolylineDistance(anchor, line) < ownDist) return true;
  }
  return false;
}

/** Shortest distance from `p` to any point of the polyline's painted stroke. */
function pointToPolylineDistance(p: Point, line: EdgeLine): number {
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i < line.points.length - 1; i++) {
    const d = pointToSegmentDistance(p, line.points[i], line.points[i + 1]);
    if (d < best) best = d;
  }
  return Math.max(0, best - line.halfStroke);
}

/** Shortest distance from `p` to the segment `a`–`b` (not the infinite line). */
function pointToSegmentDistance(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-12) return Math.hypot(p.x - a.x, p.y - a.y);
  // Project p onto the segment, clamped to its ends.
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Shortest distance from `p` to a rectangle; 0 when `p` is inside it. */
function pointToRectDistance(p: Point, r: Rect): number {
  const dx = Math.max(r.x - p.x, 0, p.x - (r.x + r.width));
  const dy = Math.max(r.y - p.y, 0, p.y - (r.y + r.height));
  return Math.hypot(dx, dy);
}

/** Unit vector perpendicular to `dir`. Falls back to straight up for a zero-length chord (self-loop). */
function perpendicular(dir: Point): Point {
  const len = Math.hypot(dir.x, dir.y);
  if (len < 1e-6) return { x: 0, y: -1 };
  // Rotate the chord 90°: (dx, dy) -> (-dy, dx), then normalise.
  return { x: -dir.y / len, y: dir.x / len };
}

/**
 * Resolve auto label positions. Returns a map `edgeIndex -> anchor` containing
 * **only** labels that were nudged off their default; edges absent from the map
 * keep their default anchor (byte-stable). Deterministic: candidates are tried
 * in a fixed order and labels processed by ascending index.
 *
 * `frames` are the borders of the frames drawn on the canvas (#3155). A label
 * already clear of every border keeps its default; one whose default box
 * straddles a border is moved off it like any other collision.
 */
export function resolveLabelPlacements(
  labels: LabelInput[],
  nodeRects: Rect[],
  edgeLines: EdgeLine[] = [],
  frames: FrameBorder[] = [],
  options: LabelPlacementOptions = {},
): Map<number, Point> {
  const maxSteps = options.maxSteps ?? DEFAULT_MAX_STEPS;
  const overrides = new Map<number, Point>();

  // Obstacles that a moving label must avoid: node cards, plus the boxes of
  // labels already committed (fixed author labels first, then earlier auto ones).
  // Indexed by bounds so a candidate box is tested only against the obstacles
  // near it (#2760); the index spans the cards and every label's default box.
  const byIndex = [...labels].sort(
    (a, b) => Number(a.late === true) - Number(b.late === true) || a.index - b.index,
  );
  const obstacles = new ObstacleIndex(
    nodeRects,
    unionBounds([
      ...nodeRects,
      ...byIndex.map((label) => labelBox(label.anchor, label.width, label.fontSize)),
    ]),
  );
  const lines = new LineIndex(edgeLines);

  // Fixed (author-positioned) labels are immovable obstacles — commit them first
  // so eligible labels route around them, regardless of index order.
  for (const label of byIndex) {
    if (!label.eligible) {
      obstacles.add(labelBox(label.anchor, label.width, label.fontSize));
    }
  }

  for (const label of byIndex) {
    // The verdict belongs to this run. Inputs placed a second time (against
    // other obstacles) must not carry the previous run's answer.
    label.deferred = undefined;
    if (!label.eligible) continue;
    const step = label.fontSize + 4;
    // Two search axes: `perp` lifts the label off its edge; `tang` slides it
    // *along* the edge to a clear stretch. A single perpendicular axis is not
    // enough — for a vertical edge boxed in by side-by-side nodes the only
    // escape is along the edge, not across it (#2048). The default (0,0) is
    // always the first candidate, so a clear label is never moved (byte-stable).
    const perp = perpendicular(label.dir);
    const tang = normalize(label.dir);
    const candidates = candidateOffsets(maxSteps);
    // The label's own line, which must not push it away (a label is meant to sit
    // on the line it names) and which it must stay nearest to (#2360).
    const ownLine = edgeLines.find((line) => line.index === label.index);
    // Only lines the bounded search can actually reach. Without this every one
    // of the ~169 candidates rescans every line in the diagram, which is
    // quadratic in edge count on a dense graph. See `searchReach`.
    const reachable = reachableLines(edgeLines, label, maxSteps * step);
    lines.restrictTo(reachable);
    const defaultBox = labelBox(label.anchor, label.width, label.fontSize);
    const nearFrames = reachableFrames(frames, defaultBox, maxSteps * step);

    let bestAnchor = label.anchor;
    let bestBox = defaultBox;
    let bestCost = Number.POSITIVE_INFINITY;
    let bestDist = 0;
    for (const [i, j] of candidates) {
      const dp = i * step;
      const dt = j * step;
      const anchor: Point = {
        x: label.anchor.x + perp.x * dp + tang.x * dt,
        y: label.anchor.y + perp.y * dp + tang.y * dt,
      };
      const box = labelBox(anchor, label.width, label.fontSize);
      const cost = candidateCost(
        box,
        anchor,
        label,
        obstacles,
        lines,
        reachable,
        ownLine,
        nearFrames,
        bestCost,
      );
      if (cost === 0) {
        // Candidates are ordered by increasing displacement, so the first clear
        // one is the smallest move (and (0,0) is first → a clear default stays put).
        bestAnchor = anchor;
        bestBox = box;
        bestCost = 0;
        bestDist = i * i + j * j;
        break;
      }
      // Best-effort tiebreak: strictly fewer collisions wins. Equal cost never
      // wins because `dist` is exactly the key `candidateOffsets` sorts by, so a
      // later candidate is never nearer — which is also what lets
      // `candidateCost` stop counting at `bestCost` without changing the result.
      const dist = i * i + j * j;
      if (cost < bestCost) {
        bestCost = cost;
        bestAnchor = anchor;
        bestBox = box;
        bestDist = dist;
      }
    }

    // `label-display: auto` (#3022): a label is drawn only where it can be
    // read. When even the best candidate still lands on a card, a label or a
    // foreign line, leave it off the canvas instead of drawing it there, and
    // do not let it obstruct the labels that follow. The authored text stays
    // on the edge for the viewer to disclose.
    //
    // Ambiguity alone is not a reason to leave a label off. A label that is
    // clear but nearer another edge's line than its own is still readable, and
    // it was drawn before this property existed; ambiguity steers where a
    // label goes (#2360), it does not decide whether it is drawn.
    if (bestCost >= COLLISION_COST && label.deferrable) {
      label.deferred = true;
      continue;
    }
    obstacles.add(bestBox);
    if (bestDist !== 0) overrides.set(label.index, bestAnchor);
  }

  return overrides;
}

/**
 * Integer (perp, tang) step offsets to try, ordered by increasing Euclidean
 * displacement (so the first clearing candidate is the smallest move) with a
 * deterministic tie-break. `[0, 0]` is always first. Cached per `maxSteps`.
 */
const candidateCache = new Map<number, [number, number][]>();
function candidateOffsets(maxSteps: number): [number, number][] {
  const cached = candidateCache.get(maxSteps);
  if (cached) return cached;
  const offsets: [number, number][] = [];
  for (let i = -maxSteps; i <= maxSteps; i++) {
    for (let j = -maxSteps; j <= maxSteps; j++) offsets.push([i, j]);
  }
  offsets.sort((a, b) => {
    const da = a[0] * a[0] + a[1] * a[1];
    const db = b[0] * b[0] + b[1] * b[1];
    if (da !== db) return da - db;
    // Prefer a perpendicular lift over an along-edge slide at equal distance
    // (keeps the label nearer its midpoint), then a fully deterministic order.
    if (Math.abs(a[1]) !== Math.abs(b[1])) return Math.abs(a[1]) - Math.abs(b[1]);
    if (a[0] !== b[0]) return a[0] - b[0];
    return a[1] - b[1];
  });
  candidateCache.set(maxSteps, offsets);
  return offsets;
}

/** Unit vector along `dir`. Falls back to horizontal for a zero-length chord. */
function normalize(dir: Point): Point {
  const len = Math.hypot(dir.x, dir.y);
  if (len < 1e-6) return { x: 1, y: 0 };
  return { x: dir.x / len, y: dir.y / len };
}
